import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Move } from "@/types";
import { useGetMoveQuery } from "@/store/moves/apis";
import { useEffect, useRef, useState } from "react";
import { SheetClose } from "@/components/ui/sheet";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PlusCircle, CreditCard } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useCreateChargeMutation } from "@/store/charges/apis";
import Header from "../Header";
import GetCard from "./GetCard";
import { PaymentIntent } from "@/types";
import { getSelectedMover } from "@/store/mover/slice";
import { useSelector } from "react-redux";
import { Switch } from "@/components/ui/switch";
import AuthorizeNetCard from "@/pages/Payments/authorize-net/AuthorizeNetCard";

type BillingInfo = {
  name?: string;
  address?: string;
  zip?: string;
  country?: string;
};

type FormData = {
  move_id: number | undefined;
  amount: string;
  purpose: string;
  memo: string;
  payment_method_id?: number;
  payment_method_token?: string;
  addProcessingFee: boolean;
  processingFee: string;
  billing_info: BillingInfo;
};

const formSchema = z.object({
  move_id: z.number().min(1, "Move ID is required"),
  amount: z.string().min(1, "Amount required"),
  purpose: z
    .string()
    .refine(
      (val) => ["deposit", "final_payment", "additional_payment"].includes(val),
      {
        message: "Purpose required",
      }
    ),
  memo: z.string().optional(),
  addProcessingFee: z.boolean(),
  processingFee: z.string().optional(),
});

export function RunCharge({
  initialMove,
  refetchMove,
  onSuccess,
}: {
  initialMove?: Move;
  refetchMove: () => void;
  onSuccess: () => void;
}) {
  const { id } = useParams();
  const mover = useSelector(getSelectedMover);
  const [createCharge] = useCreateChargeMutation();
  const [move, setMove] = useState<Move>();
  const closeRef = useRef<HTMLButtonElement>(null);
  const { data: fetchedMove } = useGetMoveQuery(id, { skip: !!initialMove });
  const processor = mover?.payment_configuration.processor;

  const form = useForm<FormData>({
    defaultValues: {
      move_id: move?.id,
      amount: "",
      purpose: "",
      memo: "",
      addProcessingFee: false,
      processingFee: String(mover?.mover_configuration?.cc_fee_percentage || 0),
      billing_info: {
        name: "",
        address: "",
        zip: "",
        country: "",
      },
    },
  });
  // const [cardInputReady, setCardInputReady] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!initialMove && !fetchedMove) {
      return;
    }

    setMove(initialMove || fetchedMove);
  }, [initialMove, fetchedMove]);

  function showCardInput() {
    const elem = document.getElementById("new-card-input");
    if (elem) {
      elem.classList.remove("hidden");
    }
  }

  useEffect(() => {
    if (!move) {
      return;
    }

    if (
      !move?.rechargeable_payment_methods ||
      move?.rechargeable_payment_methods.length === 0
    ) {
      showCardInput();
    }
  }, [move]);

  useEffect(() => {
    if (move) {
      form.reset({
        move_id: move.id,
        amount: "",
        purpose: "",
        memo: "",
        addProcessingFee: false,
        processingFee: String(
          mover?.mover_configuration?.cc_fee_percentage || 0
        ),
      });
    }
  }, [move, form]);

  const okToCharge = (data: FormData) => {
    switch (processor) {
      case "remedy_authorize":
        return data.payment_method_token || data.payment_method_id;
      case "fiserv_fwd":
        return (
          paymentIntent && (data.payment_method_token || data.payment_method_id)
        );
      default:
        return false;
    }
  };

  const validateAndSubmit = async (data: FormData) => {
    setErrorMessage(null);

    if (!move) {
      return;
    }

    setSubmitting(true);

    if (!okToCharge(data)) {
      setErrorMessage("Card details are required");
      setSubmitting(false);
      return;
    }

    const validationResult = formSchema.safeParse(data);

    if (!validationResult.success) {
      const errors = validationResult.error.formErrors.fieldErrors;
      Object.entries(errors).forEach(([key, value]) => {
        form.setError(key as keyof typeof errors, {
          type: "manual",
          message: value?.[0],
        });
      });
      setSubmitting(false);
      return;
    }

    const processingFeeAmount = calculatedProcessingFee(
      parseFloat(data.amount),
      parseFloat(data.processingFee || "0"),
      data.addProcessingFee
    );

    try {
      await createCharge({
        payment_intent_id: paymentIntent?.id,
        move_id: move.id,
        payment_method_id: data.payment_method_id,
        payment_method_token: data.payment_method_token,
        amount: parseFloat(data.amount) + processingFeeAmount,
        processing_fee_percentage: data.addProcessingFee
          ? parseFloat(data.processingFee || "0")
          : 0,
        processing_fee_amount: processingFeeAmount,
        purpose: data.purpose,
        memo: data.memo,
        bill_to: {
          name: data.billing_info?.name || "",
          address: data.billing_info?.address || "",
          zip: data.billing_info?.zip || "",
          country: data.billing_info?.country || "",
        },
      }).unwrap();

      onSuccess();
      closeRef.current?.click();
      refetchMove();
    } catch (error) {
      console.error("Failed to create charge:", error);
      setErrorMessage("Failed to process payment. Please try again.");
      setSubmitting(false);
    }
  };

  function setToken(token: string) {
    form.setValue("payment_method_token", token);
  }

  function setBillingInfo(billingInfo: BillingInfo) {
    form.setValue("billing_info", billingInfo);
  }

  function handlePaymentIntent(pi: PaymentIntent) {
    setPaymentIntent(pi);
  }

  // async function initFwdCardComponent(paymentIntent: PaymentIntent) {
  //   try {
  //     const paymentForm = await (window as any).Forward.createPaymentElement({
  //       apiKey: "test_pkey_eh7gKYeiWO0IDqzfecmDQA69eUQvJY",
  //       clientSecret: paymentIntent.client_secret,
  //     });

  //     paymentForm.mount("#payment-form", {
  //       onReady: (tokenize: () => void) => {
  //         tokenize();
  //       },
  //       onSuccess: async ({ token }: { token: string }) => {
  //         const pm = await createPaymentMethod({
  //           move_id: move.id,
  //           token,
  //           payment_intent_id: paymentIntent.id,
  //         }).unwrap();
  //         form.setValue("payment_method_id", pm.id);
  //       },
  //       onError: (error: any) => {
  //         console.error("Error initializing Forward payment:", error);
  //       },
  //     });
  //   } catch (error) {
  //     console.error("Error initializing Forward payment:", error);
  //   }
  // }

  // useEffect(() => {
  //   const paymentForm = document.getElementById("payment-form");
  //   if (!paymentForm) return;

  //   const observer = new MutationObserver((mutations) => {
  //     for (const mutation of mutations) {
  //       if (mutation.addedNodes.length) {
  //         const iframe = paymentForm.querySelector("iframe");
  //         if (iframe) {
  //           setCardInputReady(true);
  //           observer.disconnect();
  //           break;
  //         }
  //       }
  //     }
  //   });

  //   observer.observe(paymentForm, {
  //     childList: true,
  //     subtree: true,
  //   });

  //   return () => observer.disconnect();
  // }, []);

  const submitDisabled = () => {
    return (
      (!form.getValues("payment_method_id") &&
        !form.getValues("payment_method_token")) ||
      submitting
    );
  };

  const calculatedProcessingFee = (
    amount: number,
    percentage = 0,
    addProcessingFee = false
  ) => {
    if (!addProcessingFee) {
      return 0;
    }

    return (percentage / 100) * amount;
  };

  if (!move || !mover) {
    return null;
  }

  return (
    <div>
      {fetchedMove && <Header move={fetchedMove} />}
      <h1 className="text-base font-semibold mb-4">
        Process a credit card charge
      </h1>
      <Form {...form}>
        <div>
          <form
            onSubmit={form.handleSubmit(validateAndSubmit)}
            className="space-y-6"
          >
            <div className="flex flex-row gap-4">
              <FormField
                control={form.control}
                name="purpose"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purpose</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select purpose" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="deposit">Deposit</SelectItem>
                        <SelectItem value="final_payment">
                          Final Payment
                        </SelectItem>
                        <SelectItem value="additional_payment">
                          Additional Payment
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount to charge</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-0 inset-y-0 flex items-center px-2 text-sm">
                          $
                        </span>
                        <Input
                          type="text"
                          className="pl-6 text-sm w-32"
                          {...field}
                          onChange={(e) => {
                            // Remove any non-digit characters except decimal point
                            const value = e.target.value.replace(/[^\d.]/g, "");

                            // Handle multiple decimal points
                            const parts = value.split(".");
                            if (parts.length > 2) {
                              field.onChange(
                                parts[0] + "." + parts.slice(1).join("")
                              );
                            } else {
                              field.onChange(value);
                            }
                          }}
                          value={
                            field.value
                              ? field.value.includes(".") &&
                                (field.value.endsWith(".") ||
                                  field.value.split(".")[1]?.length <= 2)
                                ? field.value // Keep raw value while typing decimal or up to 2 places
                                : new Intl.NumberFormat("en-US", {
                                    maximumFractionDigits: 2,
                                  }).format(parseFloat(field.value) || 0)
                              : ""
                          }
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-1">
              <FormField
                control={form.control}
                name="addProcessingFee"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormLabel>Add Credit Card Processing Fee</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(value) => {
                          if (!value) {
                            form.setValue("processingFee", "0");
                          }
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="text-yellow-600 text-sm">
                Please check that customer is paying with a credit card.
                Surcharges on debit cards are unlawful in most states.
              </div>
            </div>

            {form.watch("addProcessingFee") && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="processingFee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credit Card Surcharge</FormLabel>
                      <FormControl>
                        <div className="relative w-24">
                          <Input
                            type="text"
                            autoComplete="off"
                            className="pr-6 text-sm"
                            onFocus={(e) => {
                              e.target.select();
                            }}
                            {...field}
                          />
                          <span className="absolute right-2 inset-y-0 flex items-center text-sm">
                            %
                          </span>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-normal">Amount</span>
                    <span className="font-normal">
                      {Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(parseFloat(form.watch("amount")) || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <div className="font-normal">Processing Fee</div>
                    <div className="font-normal space-x-2">
                      <span>
                        {form.watch("addProcessingFee") &&
                          `(${form.watch("processingFee")}%)`}
                      </span>
                      <span>
                        {form.watch("addProcessingFee")
                          ? Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(
                              calculatedProcessingFee(
                                parseFloat(form.watch("amount") || "0"),
                                parseFloat(form.watch("processingFee") || "0"),
                                form.watch("addProcessingFee")
                              )
                            )
                          : "$0"}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm border-t border-gray-200 my-1 py-1">
                    <span className="font-medium">Total Amount</span>
                    <span className="font-medium">
                      {Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(
                        parseFloat(form.watch("amount") || "0") +
                          calculatedProcessingFee(
                            parseFloat(form.watch("amount") || "0"),
                            parseFloat(form.watch("processingFee") || "0"),
                            form.watch("addProcessingFee")
                          )
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="payment_method_id"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  {/* <FormLabel>Payment Method</FormLabel> */}
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      className="grid gap-2"
                    >
                      {move?.rechargeable_payment_methods?.map((pm) => (
                        <div key={pm.id}>
                          <RadioGroupItem
                            value={pm.id.toString()}
                            id={pm.id.toString()}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={pm.id.toString()}
                            className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                          >
                            <CreditCard className="h-4 w-4" />
                            <div className="flex-1 font-semibold">
                              {pm.brand.charAt(0).toUpperCase() +
                                pm.brand.slice(1)}{" "}
                              ending in {pm.last_4}{" "}
                              <span className="text-xs text-gray-800 font-normal">
                                (expires {pm.exp_month}/{pm.exp_year})
                              </span>
                            </div>
                          </Label>
                        </div>
                      ))}

                      {move?.rechargeable_payment_methods &&
                        move?.rechargeable_payment_methods.length > 0 && (
                          <div>
                            <Label
                              htmlFor="new"
                              className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                            >
                              <PlusCircle className="h-4 w-4" />
                              <div
                                className="flex-1"
                                onClick={() => showCardInput()}
                              >
                                Add New Card
                              </div>
                            </Label>
                          </div>
                        )}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div id="new-card-input" className="hidden">
              {mover.payment_configuration.processor === "remedy_authorize" && (
                <AuthorizeNetCard
                  handleToken={setToken}
                  handleBillingInfo={setBillingInfo}
                  authorizeLoginId={
                    mover.payment_configuration.authorize_login_id
                  }
                  authorizeClientKey={
                    mover.payment_configuration.authorize_client_key
                  }
                />
              )}
              {mover.payment_configuration.processor === "fiserv_fwd" && (
                <GetCard
                  amount={parseFloat(form.getValues("amount"))}
                  handleToken={setToken}
                  handlePaymentIntent={handlePaymentIntent}
                  moverId={mover.id}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="memo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row justify-between">
                    Notes
                    <span className="text-xs text-gray-700">optional</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter additional information about the payment"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-4">
              {errorMessage && (
                <div className="text-sm text-red-600 font-medium">
                  {errorMessage}
                </div>
              )}
              <div className="flex gap-4">
                <Button
                  type="button"
                  disabled={submitDisabled()}
                  onClick={() => form.handleSubmit(validateAndSubmit)()}
                >
                  {submitting ? "Processing..." : "Run Charge"}
                </Button>
                <SheetClose ref={closeRef} asChild>
                  <Button type="button" variant="secondary">
                    Cancel
                  </Button>
                </SheetClose>
              </div>
            </div>
          </form>
        </div>
        {/* <div className="hidden">
            <h1 className="text-xl font-semibold mb-6">Preview</h1>
            <div className="p-4 border-b md:border-r border-black">
              <div className="flex flex-row gap-4">
                <div className="w-1/2">
                  <h2 className="text-lg font-semibold">Amount</h2>
                  <p className="text-sm text-gray-500">
                    {form.getValues("amount")
                      ? Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(parseFloat(form.getValues("amount")))
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          </div> */}
      </Form>
    </div>
  );
}
